import jsQR from "jsqr";

export const formatDate = (value) => {
    if (!value)
        return '';

    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' });
};

export const formatMoney = (value) =>
    Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

// Certificado e tribunais e Pulso ficam na mesma aba: o Pulso pede para focar uma etapa (certificado ou tribunais) e
// o bloco do certificado avisa quando os dados mudam, para o checklist do Pulso acompanhar.
export const FILING_FOCUS_EVENT = 'facilita:filing-focus';
export const FILING_CHANGED_EVENT = 'facilita:filing-changed';

export const focusFilingStep = (target) =>
    window.dispatchEvent(new CustomEvent(FILING_FOCUS_EVENT, { detail: { target } }));

export const scrollToElement = (id) => {
    const element = document.getElementById(id);
    element?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    return element;
};

export const daysUntil = (value) => {
    if (!value)
        return null;

    return Math.ceil((new Date(value).getTime() - Date.now()) / (24 * 60 * 60 * 1000));
};

// Estado do certificado como o cliente entende: válido, vence logo ou vencido.
export const certificateTone = (certificate) => {
    if (!certificate)
        return { tone: 'neutral', label: 'Sem certificado' };

    if (certificate.state === 'expired')
        return { tone: 'danger', label: 'Expirado' };

    if (certificate.state === 'expiring') {
        const days = daysUntil(certificate.validUntil);
        if (days > 1)
            return { tone: 'warning', label: `Vence em ${days} dias` };

        return { tone: 'warning', label: days === 1 ? 'Vence amanhã' : 'Vence hoje' };
    }

    return { tone: 'success', label: `Válido até ${formatDate(certificate.validUntil)}` };
};

const EXPIRY_NOTICE_DAYS = 30;
const EXPIRY_URGENT_DAYS = 7;

export const expiresInDaysOf = (certificate) => {
    if (!certificate)
        return null;
    const days = Number.isFinite(Number(certificate.expiresInDays)) && certificate.expiresInDays !== null
        ? Number(certificate.expiresInDays)
        : daysUntil(certificate.expiresAt || certificate.validUntil);
    return Number.isFinite(days) ? days : null;
};

// Aviso de vencimento: a partir de 30 dias, urgente nos últimos 7. Vencido tem aviso próprio.
export const expiryNotice = (certificate) => {
    const days = expiresInDaysOf(certificate);
    if (days === null || days > EXPIRY_NOTICE_DAYS || certificate.state === 'expired' || days < 0)
        return null;

    const when = days === 0 ? 'vence hoje' : days === 1 ? 'vence amanhã' : `vence em ${days} dias`;
    return { tone: days <= EXPIRY_URGENT_DAYS ? 'danger' : 'warning', days, when };
};

// Tribunais em que o advogado já pode protocolar. A API conta; sem a contagem, somamos pelos acessos prontos.
export const connectedCourtsOf = (certificate, accessGroups = []) => {
    if (!certificate)
        return 0;
    if (Number.isFinite(Number(certificate.connectedCourts)) && certificate.connectedCourts !== null)
        return Number(certificate.connectedCourts);
    return accessGroups
        .filter(group => certificate.access?.[group.key]?.status === 'ready')
        .reduce((sum, group) => sum + (group.courts || []).length, 0);
};

const LOWERCASE_PARTICLES = ['da', 'das', 'de', 'do', 'dos', 'e'];

// O certificado traz o nome em maiúsculas ("MARINA COSTA LIMA"); na tela vira "Marina Costa Lima".
export const titleCaseName = (name) => String(name || '').trim().toLowerCase().split(/\s+/).filter(Boolean)
    .map((word, index) => (index > 0 && LOWERCASE_PARTICLES.includes(word) ? word : word.charAt(0).toUpperCase() + word.slice(1)))
    .join(' ');

// O titular pode ter nome de escritório; o certificado traz o nome da pessoa.
export const personNameOf = (lawyer) => titleCaseName(lawyer?.certificate?.holderName || lawyer?.name);

export const firstNameOf = (lawyer) => personNameOf(lawyer).split(' ')[0] || '';

// Como o acesso entra e onde vale. Com um tribunal só, o nome do acesso já diz qual é (ex.: "TJSP · eproc").
export const accessSubtitle = (group) => {
    const how = group.requiresCourtLogin ? 'CPF, senha e código do celular' : group.requiresTotp ? 'Certificado + código do celular' : 'Só o certificado';
    const courts = group.courts || [];
    if (courts.length <= 1)
        return how;
    // F158: pela sigla (TJRJ, TRT-1), para o cliente ver os tribunais que o acesso cobre sem abrir nada.
    const names = [...new Set(courts.map(court => court.acronym || court.name))];
    return `${how} · ${names.length <= 3 ? names.join(', ') : `${names.slice(0, 3).join(', ')} e mais ${names.length - 3}`}`;
};

export const ACCESS_STATUS = {
    activating: { tone: 'brand', label: 'Ativando…' },
    activation_failed: { tone: 'danger', label: 'Código não ativo' },
    ready: { tone: 'success', label: 'Conectado' },
    connect: { tone: 'neutral', label: 'Não conectado' },
    expired: { tone: 'danger', label: 'Expirado' },
    // Só vira Reconectar quando o login falha num protocolo de verdade (sem verificação periódica).
    reconnect: { tone: 'danger', label: 'Reconectar' },
};

// O QR do autenticador é a própria chave desenhada: lemos o print no navegador e
// só a URI otpauth:// sobe para a API (a imagem nunca sai do computador do cliente).
export const readQrFromImage = (file) =>
    new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const image = new Image();

        image.onload = () => {
            const maxSide = 1600;
            const scale = Math.min(1, maxSide / Math.max(image.width, image.height));
            const canvas = document.createElement('canvas');
            canvas.width = Math.round(image.width * scale);
            canvas.height = Math.round(image.height * scale);

            const context = canvas.getContext('2d');
            context.drawImage(image, 0, 0, canvas.width, canvas.height);
            const pixels = context.getImageData(0, 0, canvas.width, canvas.height);
            URL.revokeObjectURL(url);

            const found = jsQR(pixels.data, pixels.width, pixels.height, { inversionAttempts: 'attemptBoth' });
            if (!found?.data)
                return reject(new Error('QR_NOT_FOUND'));

            resolve(found.data);
        };

        image.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error('IMAGE_INVALID'));
        };

        image.src = url;
    });

const BASE32_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';

const toBase32 = (bytes) => {
    let bits = '';
    for (const byte of bytes)
        bits += byte.toString(2).padStart(8, '0');

    let text = '';
    for (let offset = 0; offset < bits.length; offset += 5)
        text += BASE32_ALPHABET[parseInt(bits.slice(offset, offset + 5).padEnd(5, '0'), 2)];
    return text;
};

// Protobuf mínimo: só varint (tipo 0) e bytes (tipo 2), que é o que a exportação usa.
const readProtobuf = (bytes) => {
    const fields = [];
    let index = 0;
    const varint = () => {
        let value = 0;
        let shift = 0;
        let byte;
        do {
            if (index >= bytes.length)
                throw new Error('PROTOBUF_TRUNCATED');
            byte = bytes[index++];
            value += (byte & 0x7f) * 2 ** shift;
            shift += 7;
        } while (byte & 0x80);
        return value;
    };

    while (index < bytes.length) {
        const key = varint();
        const wire = key % 8;
        if (wire === 0)
            fields.push({ field: Math.floor(key / 8), value: varint() });
        else if (wire === 2) {
            const length = varint();
            fields.push({ field: Math.floor(key / 8), value: bytes.slice(index, index + length) });
            index += length;
        } else
            throw new Error('PROTOBUF_UNSUPPORTED');
    }
    return fields;
};

const utf8 = (bytes) =>
    bytes ? decodeURIComponent(Array.from(bytes, byte => `%${byte.toString(16).padStart(2, '0')}`).join('')) : '';

const MIGRATION_ALGORITHMS = { 1: 'SHA1', 2: 'SHA256', 3: 'SHA512' };
const MIGRATION_DIGITS = { 1: 6, 2: 8 };
const MIGRATION_TOTP = 2;

// F409: o Google Authenticator, em "Exportar contas", gera um QR otpauth-migration:// (protobuf em base64) com as
// contas do app. Com uma conta TOTP só, vira a URI otpauth://totp de sempre; com várias, não dá para saber qual é a
// do tribunal.
export const parseMigrationQr = (text) => {
    try {
        const data = new URL(text).searchParams.get('data');
        if (!data)
            return null;

        const binary = atob(data.replace(/ /g, '+').replace(/-/g, '+').replace(/_/g, '/'));
        const bytes = Uint8Array.from(binary, char => char.charCodeAt(0));

        const accounts = readProtobuf(bytes)
            .filter(item => item.field === 1 && typeof item.value !== 'number')
            .map(item => Object.fromEntries(readProtobuf(item.value).map(({ field, value }) => [field, value])))
            .filter(account => account[6] === MIGRATION_TOTP && account[1]?.length);
        if (accounts.length !== 1)
            return accounts.length > 1 ? { multiple: true } : null;

        const account = accounts[0];
        const name = utf8(account[2]);
        const issuer = utf8(account[3]);
        const params = new URLSearchParams({ secret: toBase32(account[1]) });
        if (issuer)
            params.set('issuer', issuer);
        if (MIGRATION_ALGORITHMS[account[4]])
            params.set('algorithm', MIGRATION_ALGORITHMS[account[4]]);
        if (MIGRATION_DIGITS[account[5]])
            params.set('digits', String(MIGRATION_DIGITS[account[5]]));

        const label = [issuer, name].filter(Boolean).join(':');
        return { secret: `otpauth://totp/${encodeURIComponent(label)}?${params}`, account: label || null };
    } catch (invalid) {
        return null;
    }
};

// Aceita a URI do QR (também a exportação do Google Authenticator) ou a chave em texto (base32, com ou sem espaços).
export const parseAuthenticatorSecret = (raw) => {
    const text = String(raw || '').trim();
    if (!text)
        return null;

    if (text.toLowerCase().startsWith('otpauth-migration://')) {
        const migrated = parseMigrationQr(text);
        return migrated?.secret ? migrated : null;
    }

    if (text.toLowerCase().startsWith('otpauth://')) {
        try {
            const uri = new URL(text);
            const secret = uri.searchParams.get('secret');
            if (!secret)
                return null;

            const label = decodeURIComponent(uri.pathname.replace(/^\/+/, ''));
            return { secret: text, account: label || uri.searchParams.get('issuer') || null };
        } catch (invalid) {
            return null;
        }
    }

    const compact = text.replace(/[\s-]/g, '').toUpperCase();
    if (!/^[A-Z2-7]+=*$/.test(compact) || compact.length < 16)
        return null;

    return { secret: compact, account: null };
};
